import { useRef, useState } from 'react';
import type { InvitePreview } from '@bingo/shared';
import { copyText, selectAll } from '../lib/clipboard.js';

/**
 * O link recém-criado, pronto para sair da tela.
 *
 * Aparece **uma vez só**: o banco guarda apenas o hash do token, então este
 * é o único momento em que o link existe. Por isso o aviso em vez de um
 * botão de "ver de novo" que não teria como funcionar.
 *
 * Dois caminhos de saída porque são dois jeitos reais de mandar: o WhatsApp
 * (que é como quem organiza um bingo de fato se comunica) e o copiar, com
 * seleção de reserva para quando a área de transferência não existe — o que
 * acontece em http na rede local.
 */
export function ShareLink({
  preview,
  onClose,
  title = 'Link criado',
}: {
  preview: InvitePreview;
  onClose?: () => void;
  title?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState<'idle' | 'ok' | 'manual'>('idle');

  const copy = async () => {
    const ok = await copyText(preview.url);
    setCopied(ok ? 'ok' : 'manual');

    // Sem área de transferência, o link fica selecionado: um Ctrl+C resolve,
    // e a pessoa não fica olhando para um erro sem saída.
    if (!ok) selectAll(inputRef.current);
    window.setTimeout(() => setCopied('idle'), 4000);
  };

  const validade = new Date(preview.expiresAt).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <section className="share-link" aria-live="polite">
      <header>
        <strong>{title}</strong>
        {onClose ? (
          <button type="button" className="btn ghost" onClick={onClose} aria-label="Fechar">
            ✕
          </button>
        ) : null}
      </header>

      <p className="hint-text">
        Para <strong>{preview.email}</strong> · vale até {validade}
      </p>

      <input
        ref={inputRef}
        className="share-url"
        value={preview.url}
        readOnly
        onFocus={(event) => event.currentTarget.select()}
        aria-label="Link"
      />

      <div className="share-actions">
        <a
          className="btn primary"
          href={preview.whatsappUrl}
          target="_blank"
          rel="noreferrer noopener"
        >
          Compartilhar no WhatsApp
        </a>
        <button type="button" className="btn" onClick={() => void copy()}>
          {copied === 'ok' ? 'Copiado!' : 'Copiar'}
        </button>
      </div>

      {copied === 'manual' ? (
        <p className="hint-text">
          O navegador não deixou copiar automaticamente. O link já está selecionado — use Ctrl+C (ou
          toque e segure no celular).
        </p>
      ) : null}

      <p className="hint-text">
        Guarde agora: por segurança, o link não pode ser mostrado de novo. Se precisar, gere outro.
      </p>
    </section>
  );
}
